import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Card } from 'components/ui';
import { LAYOUT_TYPE_MODERN } from 'constants/theme.constant';
import { useSelector } from 'react-redux';

/**
 * AdaptableCard is a custom card component that adjusts its style based on the current layout type.
 * It provides options to control borders, shadows, and dividers.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional class names for the card container.
 * @param {string} [props.bodyClass] - Additional class names for the card body.
 * @param {boolean} [props.leftSideBorder] - Whether to display the left border in modern layout.
 * @param {boolean} [props.rightSideBorder] - Whether to display the right border in modern layout.
 * @param {boolean} [props.divider] - Whether to display a divider line at the bottom of the card in modern layout.
 * @param {boolean} [props.shadow] - Whether to show a shadow for the card in classic layout.
 * @param {boolean} [props.isLastChild] - Whether the card is the last child in the parent container.
 * @returns {JSX.Element} - The AdaptableCard component.
 */
const AdaptableCard = (props) => {
    const {
        className,
        children,
        bodyClass,
        leftSideBorder,
        rightSideBorder,
        divider,
        shadow,
        isLastChild,
        ...rest
    } = props;

    const type = useSelector((state) => state.theme.layout.type);

    return (
        <Card
            className={classNames(
                className,
                type === LAYOUT_TYPE_MODERN && 'border-0',
                type === LAYOUT_TYPE_MODERN &&
                    rightSideBorder &&
                    'ltr:border-r-0 rtl:border-l-0 ltr:md:border-r rtl:md:border-l md:border-gray-200 md:dark:border-gray-600 rounded-tr-none rounded-br-none rtl:rounded-tr-none rtl:rounded-br-none',
                type === LAYOUT_TYPE_MODERN &&
                    leftSideBorder &&
                    'ltr:border-l-0 rtl:border-r-0 ltr:md:border-l rtl:md:border-r md:border-gray-200 md:dark:border-gray-600 rounded-tl-none rounded-bl-none rtl:rounded-tl-none rtl:rounded-bl-none',
                type === LAYOUT_TYPE_MODERN &&
                    divider &&
                    `${
                        !isLastChild ? 'border-b pb-6' : ''
                    } py-4 md:border-gray-200 md:dark:border-gray-600 rounded-br-none rounded-bl-none`,
                type !== LAYOUT_TYPE_MODERN &&
                    shadow &&
                    'rounded-none shadow-none border-0'
            )}
            {...rest}
            bodyClass={classNames(
                type === LAYOUT_TYPE_MODERN ? 'card-gutterless' : '',
                bodyClass
            )}
        >
            {children}
        </Card>
    );
};

AdaptableCard.propTypes = {
    leftSideBorder: PropTypes.bool,
    rightSideBorder: PropTypes.bool,
    divider: PropTypes.bool,
    shadow: PropTypes.bool,
    isLastChild: PropTypes.bool,
};

export default AdaptableCard;
